import React, { createContext, useContext, useCallback } from "react";

const translations = {
  fr: {
    appName: "Facturation",
    nav: { dashboard: "Tableau de bord", customers: "Clients", products: "Produits & Services", quotes: "Devis", invoices: "Factures", settings: "Paramètres" },
    common: {
      create: "Créer", edit: "Modifier", delete: "Supprimer", save: "Enregistrer", cancel: "Annuler",
      search: "Rechercher", actions: "Actions", confirm: "Confirmer", view: "Voir", back: "Retour",
      none: "Aucun résultat", loading: "Chargement…", downloadPdf: "Télécharger le PDF", preview: "Aperçu PDF",
      status: "Statut", date: "Date", total: "Total", name: "Nom", required: "Ce champ est requis",
      deleteConfirm: "Voulez-vous vraiment supprimer cet élément ?", yes: "Oui", no: "Non",
    },
    status: { draft: "Brouillon", sent: "Envoyé", accepted: "Accepté", rejected: "Refusé", unpaid: "Impayée", paid: "Payée", overdue: "En retard" },
    dashboard: {
      title: "Tableau de bord", revenue: "Chiffre d'affaires", outstanding: "En attente",
      invoices: "Factures", quotes: "Devis", customers: "Clients", paid: "Payées", unpaid: "Impayées",
      monthlyRevenue: "Chiffre d'affaires mensuel", recentInvoices: "Factures récentes", noData: "Pas encore de données",
    },
    customer: {
      title: "Clients", new: "Nouveau client", edit: "Modifier le client", name: "Nom / Société",
      email: "E-mail", phone: "Téléphone", address: "Adresse", postalCode: "Code postal", city: "Ville",
      country: "Pays", taxId: "N° TVA", empty: "Aucun client. Ajoutez votre premier client.",
    },
    product: {
      title: "Produits & Services", new: "Nouveau produit", edit: "Modifier le produit",
      name: "Désignation", description: "Description", unitPrice: "Prix unitaire", taxRate: "Taux TVA (%)",
      unit: "Unité", empty: "Aucun produit. Ajoutez votre premier produit.",
    },
    doc: {
      quoteNew: "Nouveau devis", invoiceNew: "Nouvelle facture", quoteEdit: "Modifier le devis", invoiceEdit: "Modifier la facture",
      customer: "Client", selectCustomer: "Sélectionner un client", issueDate: "Date d'émission",
      dueDate: "Date d'échéance", validUntil: "Valable jusqu'au", serviceDate: "Date de prestation", notes: "Remarques", items: "Lignes",
      addItem: "Ajouter une ligne", addProduct: "Ajouter un produit", description: "Description", qty: "Qté",
      unitPrice: "Prix unit.", taxRate: "TVA %", lineTotal: "Montant", subtotal: "Sous-total", tax: "TVA",
      number: "Numéro", convert: "Convertir en facture", pay: "Payer maintenant", markPaid: "Marquer payée",
      emptyQuotes: "Aucun devis pour l'instant.", emptyInvoices: "Aucune facture pour l'instant.",
      noItems: "Ajoutez au moins une ligne.", converted: "Devis converti en facture.",
      whatsapp: "WhatsApp Business", email: "E-mail", shareHello: "Bonjour, veuillez trouver votre document ci-joint :", paymentTerms: "Conditions de paiement",
    },
    settings: {
      title: "Paramètres de l'entreprise", company: "Nom de l'entreprise", bank: "Coordonnées bancaires",
      iban: "IBAN", bankName: "Banque", defaultTax: "Taux de TVA par défaut (%)", saved: "Paramètres enregistrés.",
      logo: "Logo", uploadLogo: "Téléverser un logo", logoHint: "PNG ou JPG, max 2 Mo. Affiché sur vos devis et factures.", logoSaved: "Logo enregistré.",
    },
    payment: {
      success: "Paiement réussi", successMsg: "Merci ! Votre paiement a bien été reçu.",
      cancel: "Paiement annulé", cancelMsg: "Le paiement a été annulé. Vous pouvez réessayer.",
      checking: "Vérification du paiement…", backInvoices: "Retour aux factures",
    },
  },
  de: {
    appName: "Rechnungen",
    nav: { dashboard: "Übersicht", customers: "Kunden", products: "Produkte & Dienstleistungen", quotes: "Angebote", invoices: "Rechnungen", settings: "Einstellungen" },
    common: {
      create: "Erstellen", edit: "Bearbeiten", delete: "Löschen", save: "Speichern", cancel: "Abbrechen",
      search: "Suchen", actions: "Aktionen", confirm: "Bestätigen", view: "Ansehen", back: "Zurück",
      none: "Keine Ergebnisse", loading: "Laden…", downloadPdf: "PDF herunterladen", preview: "PDF-Vorschau",
      status: "Status", date: "Datum", total: "Gesamt", name: "Name", required: "Pflichtfeld",
      deleteConfirm: "Diesen Eintrag wirklich löschen?", yes: "Ja", no: "Nein",
    },
    status: { draft: "Entwurf", sent: "Gesendet", accepted: "Angenommen", rejected: "Abgelehnt", unpaid: "Offen", paid: "Bezahlt", overdue: "Überfällig" },
    dashboard: {
      title: "Übersicht", revenue: "Umsatz", outstanding: "Offene Beträge",
      invoices: "Rechnungen", quotes: "Angebote", customers: "Kunden", paid: "Bezahlt", unpaid: "Offen",
      monthlyRevenue: "Monatlicher Umsatz", recentInvoices: "Letzte Rechnungen", noData: "Noch keine Daten",
    },
    customer: {
      title: "Kunden", new: "Neuer Kunde", edit: "Kunde bearbeiten", name: "Name / Firma",
      email: "E-Mail", phone: "Telefon", address: "Adresse", postalCode: "PLZ", city: "Ort",
      country: "Land", taxId: "USt-IdNr.", empty: "Keine Kunden. Fügen Sie Ihren ersten Kunden hinzu.",
    },
    product: {
      title: "Produkte & Dienstleistungen", new: "Neues Produkt", edit: "Produkt bearbeiten",
      name: "Bezeichnung", description: "Beschreibung", unitPrice: "Einzelpreis", taxRate: "MwSt.-Satz (%)",
      unit: "Einheit", empty: "Keine Produkte. Fügen Sie Ihr erstes Produkt hinzu.",
    },
    doc: {
      quoteNew: "Neues Angebot", invoiceNew: "Neue Rechnung", quoteEdit: "Angebot bearbeiten", invoiceEdit: "Rechnung bearbeiten",
      customer: "Kunde", selectCustomer: "Kunde auswählen", issueDate: "Ausstellungsdatum",
      dueDate: "Fälligkeitsdatum", validUntil: "Gültig bis", serviceDate: "Leistungsdatum", notes: "Anmerkungen", items: "Positionen",
      addItem: "Position hinzufügen", addProduct: "Produkt hinzufügen", description: "Beschreibung", qty: "Menge",
      unitPrice: "Einzelpreis", taxRate: "MwSt. %", lineTotal: "Betrag", subtotal: "Zwischensumme", tax: "MwSt.",
      number: "Nummer", convert: "In Rechnung umwandeln", pay: "Jetzt bezahlen", markPaid: "Als bezahlt markieren",
      emptyQuotes: "Noch keine Angebote.", emptyInvoices: "Noch keine Rechnungen.",
      noItems: "Fügen Sie mindestens eine Position hinzu.", converted: "Angebot in Rechnung umgewandelt.",
      whatsapp: "WhatsApp Business", email: "E-Mail", shareHello: "Guten Tag, anbei finden Sie Ihr Dokument:", paymentTerms: "Zahlungsbedingung",
    },
    settings: {
      title: "Unternehmenseinstellungen", company: "Firmenname", bank: "Bankverbindung",
      iban: "IBAN", bankName: "Bank", defaultTax: "Standard-MwSt.-Satz (%)", saved: "Einstellungen gespeichert.",
      logo: "Logo", uploadLogo: "Logo hochladen", logoHint: "PNG oder JPG, max. 2 MB. Erscheint auf Ihren Angeboten und Rechnungen.", logoSaved: "Logo gespeichert.",
    },
    payment: {
      success: "Zahlung erfolgreich", successMsg: "Vielen Dank! Ihre Zahlung ist eingegangen.",
      cancel: "Zahlung abgebrochen", cancelMsg: "Die Zahlung wurde abgebrochen. Sie können es erneut versuchen.",
      checking: "Zahlung wird geprüft…", backInvoices: "Zurück zu den Rechnungen",
    },
  },
  en: {
    appName: "Invoicing",
    nav: { dashboard: "Dashboard", customers: "Customers", products: "Products & Services", quotes: "Quotes", invoices: "Invoices", settings: "Settings" },
    common: {
      create: "Create", edit: "Edit", delete: "Delete", save: "Save", cancel: "Cancel",
      search: "Search", actions: "Actions", confirm: "Confirm", view: "View", back: "Back",
      none: "No results", loading: "Loading…", downloadPdf: "Download PDF", preview: "PDF preview",
      status: "Status", date: "Date", total: "Total", name: "Name", required: "This field is required",
      deleteConfirm: "Do you really want to delete this item?", yes: "Yes", no: "No",
    },
    status: { draft: "Draft", sent: "Sent", accepted: "Accepted", rejected: "Rejected", unpaid: "Unpaid", paid: "Paid", overdue: "Overdue" },
    dashboard: {
      title: "Dashboard", revenue: "Revenue", outstanding: "Outstanding",
      invoices: "Invoices", quotes: "Quotes", customers: "Customers", paid: "Paid", unpaid: "Unpaid",
      monthlyRevenue: "Monthly revenue", recentInvoices: "Recent invoices", noData: "No data yet",
    },
    customer: {
      title: "Customers", new: "New customer", edit: "Edit customer", name: "Name / Company",
      email: "Email", phone: "Phone", address: "Address", postalCode: "Postal code", city: "City",
      country: "Country", taxId: "VAT ID", empty: "No customers. Add your first customer.",
    },
    product: {
      title: "Products & Services", new: "New product", edit: "Edit product",
      name: "Name", description: "Description", unitPrice: "Unit price", taxRate: "VAT rate (%)",
      unit: "Unit", empty: "No products. Add your first product.",
    },
    doc: {
      quoteNew: "New quote", invoiceNew: "New invoice", quoteEdit: "Edit quote", invoiceEdit: "Edit invoice",
      customer: "Customer", selectCustomer: "Select a customer", issueDate: "Issue date",
      dueDate: "Due date", validUntil: "Valid until", serviceDate: "Service date", notes: "Notes", items: "Line items",
      addItem: "Add line", addProduct: "Add product", description: "Description", qty: "Qty",
      unitPrice: "Unit price", taxRate: "VAT %", lineTotal: "Amount", subtotal: "Subtotal", tax: "VAT",
      number: "Number", convert: "Convert to invoice", pay: "Pay now", markPaid: "Mark as paid",
      emptyQuotes: "No quotes yet.", emptyInvoices: "No invoices yet.",
      noItems: "Add at least one line item.", converted: "Quote converted to invoice.",
      whatsapp: "WhatsApp Business", email: "Email", shareHello: "Hello, please find your document attached:", paymentTerms: "Payment terms",
    },
    settings: {
      title: "Company settings", company: "Company name", bank: "Bank details",
      iban: "IBAN", bankName: "Bank", defaultTax: "Default VAT rate (%)", saved: "Settings saved.",
      logo: "Logo", uploadLogo: "Upload logo", logoHint: "PNG or JPG, max 2 MB. Shown on your quotes and invoices.", logoSaved: "Logo saved.",
    },
    payment: {
      success: "Payment successful", successMsg: "Thank you! Your payment has been received.",
      cancel: "Payment cancelled", cancelMsg: "The payment was cancelled. You can try again.",
      checking: "Checking payment…", backInvoices: "Back to invoices",
    },
  },
};

export const LANGS = [
  { code: "fr", label: "Français", flag: "FR" },
  { code: "de", label: "Deutsch", flag: "DE" },
  { code: "en", label: "English", flag: "EN" },
];

const I18nContext = createContext(null);

export const I18nProvider = ({ children }) => {
  const lang = "de";
  const setLang = useCallback(() => {}, []);

  const t = useCallback(
    (path) => {
      const parts = path.split(".");
      let cur = translations.de;
      for (const p of parts) {
        cur = cur?.[p];
        if (cur === undefined) return path;
      }
      return cur;
    },
    []
  );

  return <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
};
